/* The arithmetic behind the dashboard's shipment tiles.
 *
 * Written as CommonJS on purpose: this is imported by the app
 * (src/firebase/firebase.utils.js) *and* required by a plain Node script
 * (scripts/backfill-lot-order-totals.js), which cannot load an ES module. One
 * copy means the backfill and the app can never disagree about what a lot's
 * revenue is — which they would eventually, if this were duplicated.
 *
 * Unit tests: src/firebase/lotOrderTotals.test.js, which pins these results
 * against the exact expression dashboard.js used to evaluate inline.
 */

// Transcribed from the original dashboard expression, including details that
// look like bugs but are load-bearing for matching the old numbers:
//
//   - the filter is `order.ratePerKg` **truthiness**, so 0 and "" are excluded
//     but the string "0" is not;
//   - `finalTotal` is used exactly as stored when truthy — it is NOT passed
//     through parseInt, so a fractional invoice total keeps its fraction;
//   - only the rate × weight fallback truncates, and it truncates per order,
//     which is why summing per lot and then across lots gives an identical
//     result to summing every order at once.
const orderRevenueValue = (order) => {
  const fallback = parseInt((order.ratePerKg || 0) * (order.grossWeight || 0));
  const raw = order.finalTotal || fallback || 0;
  const value = typeof raw === "number" ? raw : parseFloat(raw);
  // Two deliberate departures from the original, neither of which changes the
  // result for numeric data: a numeric *string* is coerced rather than
  // string-concatenated into the running total, and a value that is not
  // numeric at all contributes 0 rather than turning the sum into NaN.
  return Number.isFinite(value) ? value : 0;
};

/**
 * Whether a lot is fully delivered: every one of its orders has an invoice
 * (`finalTotal`) and every one of those invoices is Paid.
 *
 * A lot with no orders is NOT delivered. "Nothing to deliver" is not the same
 * as "everything delivered", and conflating the two is what made the old
 * inline check wrong: it compared `totalMoneyToPay` against `totalRevenue`,
 * and `totalMoneyToPay` is forced to 0 when any order lacks an invoice — so a
 * lot nobody had been invoiced for compared 0 === 0 and was marked delivered.
 *
 * @param {Array} orders every order belonging to one lot
 * @returns {boolean}
 */
const lotIsFullyDelivered = (orders) => {
  const list = orders || [];
  if (!list.length) return false;
  return list.every(
    (order) => order.finalTotal && order.invoiceStatus === "Paid",
  );
};

/**
 * @param {Array} orders every order belonging to one lot
 * @returns {{revenue: number, weight: number, orderCount: number}}
 *
 * `orderCount` counts every order in the lot; revenue and weight count only
 * priced ones. That asymmetry is in the original and is preserved.
 */
const sumLotOrderTotals = (orders) => {
  const list = orders || [];
  const priced = list.filter((order) => order.ratePerKg);
  return {
    revenue: priced.reduce((acc, order) => acc + orderRevenueValue(order), 0),
    weight: priced.reduce((acc, order) => {
      const value = parseFloat(order.grossWeight || 0);
      return acc + (Number.isFinite(value) ? value : 0);
    }, 0),
    orderCount: list.length,
  };
};

module.exports = { sumLotOrderTotals, lotIsFullyDelivered };
